/**
 * WorkshopCalendarPicker — a shared, reusable month-calendar workshop picker.
 *
 * Renders a Sun–Sat month grid with prev/next month navigation; each day cell
 * shows its workshop events ({@link CalendarEvent}) as selectable pills (tree-
 * type color dot + title + subtitle + seats). Single-select via `selectedId` /
 * `onSelect`; disabled events are shown but not selectable. The component is
 * generic over the presentational `CalendarEvent` shape, so any call-site maps
 * its own domain rows onto it.
 *
 * An OPTIONAL filter bar (city + tree-type chips) is available behind
 * `enableFilters` (DEFAULT OFF) for future call-sites; it's a clean prop seam,
 * not fully wired — the cancel/rebook flow renders it OFF.
 *
 * Month math uses date-fns; events are bucketed into day cells by the UTC date
 * of `startsAt` (consistent with the rest of the app's UTC-anchored calendars).
 */
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CalendarDayOverflow } from './CalendarDayOverflow';
import type { CalendarEvent, CalendarFilterOption } from './calendarEvent';
import { splitDayEvents } from './calendarEvent';
import { CalendarEventPill } from './CalendarEventPill';
import { CalendarFilterBar } from './CalendarFilterBar';
import { DenseDayCell } from './DenseDayCell';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Uniform day-cell height (BON-142) — every cell is this tall regardless of
 *  event count, so a busy day never stretches its row (and a blank day never
 *  collapses it). Room for `MAX_VISIBLE_EVENTS` pills; the rest fold into the
 *  "+N more" affordance instead of overflowing or internally scrolling. */
const DAY_CELL_HEIGHT = 150;
// Two pills fit the fixed cell height without clipping (three overflowed the
// clipped area → a cut-off third/selected pill, BON-142); the rest fold into
// the "+N more" affordance. The richer multi-event layout is a separate design
// pass.
const MAX_VISIBLE_EVENTS = 2;

export interface WorkshopCalendarPickerProps {
  events: CalendarEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Initial month to display; defaults to the earliest event's month, else now.
   *  Ignored in controlled-month mode (see `month`/`onMonthChange`). */
  initialMonth?: Date;
  /** Controlled month (BON-142 booking picker chrome) — when BOTH `month` AND
   *  `onMonthChange` are provided, the caller owns the displayed month and its
   *  nav: the grid renders `month` as given, and the picker does NOT render
   *  its own internal `MonthHeader` (the caller renders prev/next itself,
   *  e.g. in a fixed chrome row above a scrolling body). When either is
   *  omitted, behavior is unchanged — internal state + the internal
   *  `MonthHeader` (dashboards, `RebookSelectModal`). */
  month?: Date;
  onMonthChange?: (month: Date) => void;
  /** Footer hint shown under the grid. */
  hint?: string;
  /** OFF by default. When on, renders the city + tree-type filter bar (a seam
   *  for future call-sites; the cancel flow keeps this off). */
  enableFilters?: boolean;
  /** Whether the month grid provides its OWN scroll container (default true —
   *  the dashboards/rebook call-sites embed it in a fixed box). Pass false when
   *  an ancestor already scrolls (the booking picker's modal body), so the grid
   *  doesn't add a SECOND nested scrollbar (BON-142). */
  scroll?: boolean;
  /** OFF by default — opt-in dense rendering (BON-142 booking picker): a
   *  single-event day gets a fuller tinted card; 2+ events render as capped,
   *  one-line `CompactEventPill`s with a total-count badge, and overflow hands
   *  off to `onDayOverflowClick` instead of the default `CalendarDayOverflow`
   *  popover. The rebook/dashboards calendars leave this off and are
   *  byte-for-byte unchanged. */
  dense?: boolean;
  /** Dense-mode-only seam: called with the day + ALL of that day's events when
   *  the "+N more" link or the count badge is clicked. Ignored when `dense`
   *  is off. The booking picker uses this to open a day-detail drawer. */
  onDayOverflowClick?: (day: Date, dayEvents: CalendarEvent[]) => void;
  /** Filter options + state — only meaningful when `enableFilters`. */
  cityOptions?: CalendarFilterOption[];
  treeOptions?: CalendarFilterOption[];
  activeCityId?: string | null;
  activeTreeId?: string | null;
  onCityChange?: (id: string | null) => void;
  onTreeChange?: (id: string | null) => void;
}

/** Bucket events by their WALL-CLOCK `yyyy-MM-dd`, each list sorted by start
 *  then title. The date is read straight off the ISO string (`localStartsAt`,
 *  the display value) rather than `parseISO`+`format`, which would re-anchor a
 *  `Z`-tagged naive time to the browser's timezone and shift near-midnight
 *  workshops onto the wrong day — mirrors `formatLocalTime`'s regex-the-string,
 *  never-re-anchor convention. Matches the day-cell keys (`format(day,
 *  'yyyy-MM-dd')`), which are plain calendar dates. */
function bucketByDay(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const key = e.localStartsAt.slice(0, 10);
    const list = map.get(key) ?? [];
    list.push(e);
    map.set(key, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.startsAt.localeCompare(b.startsAt) || a.title.localeCompare(b.title));
  }
  return map;
}

/** Owns the displayed month: controlled (caller passes BOTH `month` and
 *  `onMonthChange`) or uncontrolled (internal state, seeded from
 *  `initialMonth`/the earliest event, as before). Split out to keep
 *  `WorkshopCalendarPicker` itself under the complexity cap. */
function useCalendarMonth(props: WorkshopCalendarPickerProps) {
  const isControlled = props.month !== undefined && props.onMonthChange !== undefined;

  const firstMonth = useMemo(() => {
    if (props.initialMonth) return props.initialMonth;
    const earliest = props.events.map((e) => e.startsAt).sort()[0];
    return earliest ? parseISO(earliest) : new Date();
  }, [props.initialMonth, props.events]);

  const [internalMonth, setInternalMonth] = useState<Date>(firstMonth);
  const month = isControlled ? (props.month as Date) : internalMonth;

  return {
    month,
    isControlled,
    onPrev: () => setInternalMonth((m) => subMonths(m, 1)),
    onNext: () => setInternalMonth((m) => addMonths(m, 1)),
  };
}

/** Renders the picker's own prev/next header — but only in uncontrolled mode;
 *  a controlled caller owns navigation and renders its own. */
function CalendarMonthHeader({
  isControlled,
  month,
  onPrev,
  onNext,
}: {
  isControlled: boolean;
  month: Date;
  onPrev: () => void;
  onNext: () => void;
}) {
  if (isControlled) return null;
  return <MonthHeader month={month} onPrev={onPrev} onNext={onNext} />;
}

export function WorkshopCalendarPicker(props: WorkshopCalendarPickerProps) {
  const {
    events,
    selectedId,
    onSelect,
    hint,
    enableFilters = false,
    scroll = true,
    dense = false,
    onDayOverflowClick,
  } = props;

  const { month, isControlled, onPrev, onNext } = useCalendarMonth(props);
  const byDay = useMemo(() => bucketByDay(events), [events]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <CalendarMonthHeader
        isControlled={isControlled}
        month={month}
        onPrev={onPrev}
        onNext={onNext}
      />
      {enableFilters && (
        <CalendarFilterBar
          cityOptions={props.cityOptions ?? []}
          treeOptions={props.treeOptions ?? []}
          activeCityId={props.activeCityId ?? null}
          activeTreeId={props.activeTreeId ?? null}
          onCityChange={props.onCityChange ?? (() => {})}
          onTreeChange={props.onTreeChange ?? (() => {})}
        />
      )}
      <MonthGrid
        month={month}
        byDay={byDay}
        selectedId={selectedId}
        onSelect={onSelect}
        scroll={scroll}
        dense={dense}
        onDayOverflowClick={onDayOverflowClick}
      />
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--nb-subtle)', padding: '8px 2px 0' }}>{hint}</div>
      )}
    </div>
  );
}

function MonthHeader({
  month,
  onPrev,
  onNext,
}: {
  month: Date;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <button
        type="button"
        className="nb-btn nb-btn-ghost"
        aria-label="Previous month"
        onClick={onPrev}
      >
        <ChevronLeft size={14} />
      </button>
      <div style={{ fontSize: 13, fontWeight: 700, minWidth: 130, textTransform: 'uppercase' }}>
        {format(month, 'MMMM yyyy')}
      </div>
      <button
        type="button"
        className="nb-btn nb-btn-ghost"
        aria-label="Next month"
        onClick={onNext}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function MonthGrid({
  month,
  byDay,
  selectedId,
  onSelect,
  scroll,
  dense,
  onDayOverflowClick,
}: {
  month: Date;
  byDay: Map<string, CalendarEvent[]>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  scroll: boolean;
  dense: boolean;
  onDayOverflowClick?: (day: Date, dayEvents: CalendarEvent[]) => void;
}) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  });
  return (
    <div style={{ overflow: scroll ? 'auto' : 'visible' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            style={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'var(--nb-subtle)',
              padding: '4px 6px',
              textAlign: 'left',
            }}
          >
            {d}
          </div>
        ))}
        {days.map((day) =>
          dense ? (
            <DenseDayCell
              key={format(day, 'yyyy-MM-dd')}
              day={day}
              month={month}
              events={byDay.get(format(day, 'yyyy-MM-dd')) ?? []}
              selectedId={selectedId}
              onSelect={onSelect}
              onDayOverflowClick={onDayOverflowClick}
              height={DAY_CELL_HEIGHT}
            />
          ) : (
            <DayCell
              key={format(day, 'yyyy-MM-dd')}
              day={day}
              month={month}
              events={byDay.get(format(day, 'yyyy-MM-dd')) ?? []}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ),
        )}
      </div>
    </div>
  );
}

function DayCell({
  day,
  month,
  events,
  selectedId,
  onSelect,
}: {
  day: Date;
  month: Date;
  events: CalendarEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const inMonth = isSameMonth(day, month);
  const { visible, hidden } = splitDayEvents(events, MAX_VISIBLE_EVENTS);
  return (
    <div
      style={{
        height: DAY_CELL_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--nb-border)',
        margin: -0.5,
        padding: 4,
        background: inMonth ? 'transparent' : 'var(--nb-bg-2, rgba(0,0,0,.02))',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          fontSize: 10.5,
          fontWeight: 600,
          color: inMonth ? 'var(--nb-ink)' : 'var(--nb-faint)',
          marginBottom: 3,
        }}
      >
        {format(day, 'd')}
      </div>
      {/* Fixed-height, clipped events area — caps a busy day at
          MAX_VISIBLE_EVENTS pills so every cell stays the same height. */}
      <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        {visible.map((e) => (
          <CalendarEventPill
            key={e.id}
            event={e}
            selected={selectedId === e.id}
            onSelect={onSelect}
          />
        ))}
      </div>
      {/* Reserved (not clipped) so the overflow popover never gets cut off. */}
      <div style={{ flex: '0 0 auto', minHeight: 14 }}>
        {hidden.length > 0 && (
          <CalendarDayOverflow hidden={hidden} selectedId={selectedId} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}
